/**
 * Pure advance-intent resolution for BoardUi arrows / destination ghosts.
 * Prefers home lane (same column); mirrors pushOrSiege / pushPlayerPrayer targeting.
 */

import { LAYER_ORDER, layerInward, layerOutward } from "./types";
import type { LayerId, Side } from "./types";
import type { BoardState } from "./board";
import type { Presence } from "../cards/types";

/** Match BoardUi `.pushing` threshold — below this, no arrow. */
export const ADVANCE_INTENT_SHOW = 0.08;
/** Brighten / pulse destination when commit is near. */
export const ADVANCE_INTENT_IMMINENT = 0.75;

export type AdvanceIntentKind = "empty" | "clash" | "ally";

export interface AdvanceIntent {
  presenceId: string;
  side: Side;
  fromLayer: LayerId;
  fromSlot: number;
  toLayer: LayerId;
  toSlot: number;
  /** 0..1 windup */
  progress: number;
  /** What's currently in the preferred destination lane */
  kind: AdvanceIntentKind;
}

/**
 * Next layer a presence is trying to enter, or null if none (Mind prayer / Will lie).
 */
export function advanceTargetLayer(
  layer: LayerId,
  side: Side,
): LayerId | null {
  return side === "player" ? layerOutward(layer) : layerInward(layer);
}

/**
 * Preferred destination slot (stable home lane).
 */
export function advanceTargetSlot(presence: Presence, fromSlot: number): number {
  const lane = presence.homeSlot;
  if (lane >= 0 && lane <= 2) return lane;
  return Math.max(0, Math.min(2, fromSlot));
}

export function resolveAdvanceIntent(
  board: BoardState,
  layer: LayerId,
  slot: number,
  presence: Presence,
): AdvanceIntent | null {
  if (!presence.verbs.advanceAfter) return null;
  const progress = Math.max(0, Math.min(1, presence.advanceProgress));
  if (progress < ADVANCE_INTENT_SHOW) return null;

  const toLayer = advanceTargetLayer(layer, presence.side);
  if (!toLayer) return null;

  const toSlot = advanceTargetSlot(presence, slot);
  const occ = board[toLayer].slots[toSlot];
  let kind: AdvanceIntentKind = "empty";
  if (occ) {
    // Settled walks over anything in-lane — still show a clash arrow.
    kind = occ.side === presence.side ? "ally" : "clash";
  }

  return {
    presenceId: presence.instanceId,
    side: presence.side,
    fromLayer: layer,
    fromSlot: slot,
    toLayer,
    toSlot,
    progress,
    kind,
  };
}

/** Collect all visible advance intents for a board snapshot. */
export function collectAdvanceIntents(board: BoardState): AdvanceIntent[] {
  const out: AdvanceIntent[] = [];
  for (const id of LAYER_ORDER) {
    board[id].slots.forEach((p, slot) => {
      if (!p) return;
      const intent = resolveAdvanceIntent(board, id, slot, p);
      if (intent) out.push(intent);
    });
  }
  return out;
}
